import {useCallback, useState} from "react";
import {useSnackbar} from "@dain/mui-components";
import {useTranslation} from "react-i18next";
import {SubmissionDto, SubmissionFilterDto} from "../../client/submission/SubmissionApiClient";
import {PageableResultListDto} from "../../client/pagination/PageableResultListDto";
import {useClientLocator} from "../../client/ApiClientLocator";
import {CommonTranslKey} from "../../common/CommonTranslKey";
import PersonAnonymizer from "../person/PersonAnonymizer";

export enum SearchPanelState {
    INITIALIZED,
    IDLE,
    SEARCH_IN_PROGRESS
}

export enum FilterSearchType {
    NEW_SEARCH,
    NEXT_PAGE_SEARCH
}

export function useSubmissionSearch(initialFilter: SubmissionFilterDto, anonymizePersonNames: boolean = false) {
    const {submissionClient} = useClientLocator();
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    const [submissionSearchPanelState, setSubmissionSearchPanelState] = useState<SearchPanelState>(SearchPanelState.INITIALIZED);
    const [submissionFilter, setSubmissionFilter] = useState<SubmissionFilterDto>(initialFilter);
    const [displayedSubmissionsCount, setDisplayedSubmissionsCount] = useState<number>(0);

    const [submissionResults, setSubmissionResults] = useState<PageableResultListDto<SubmissionDto>>({
            items: [],
            nextPageToken: null,
        });

    const updatePaginatedSubmissions = (
        pageableSubmission: PageableResultListDto<SubmissionDto>,
        existingSubmissionResults: PageableResultListDto<SubmissionDto>,
        searchType: FilterSearchType
    ): PageableResultListDto<SubmissionDto> => {
        if (searchType === FilterSearchType.NEW_SEARCH) {
            return pageableSubmission;
        } else if (searchType === FilterSearchType.NEXT_PAGE_SEARCH) {
            return {
                items: [
                    ...existingSubmissionResults.items,
                    ...pageableSubmission.items,
                ],
                nextPageToken: pageableSubmission.nextPageToken || null,
            };
        }
        return { items: [], nextPageToken: null };
    };

    const fetchSubmissions = useCallback(
        (filter: SubmissionFilterDto, searchType: FilterSearchType) => {
            setSubmissionSearchPanelState(SearchPanelState.SEARCH_IN_PROGRESS);

            submissionClient
                .findByFilter(filter)
                .then((filteredSubmissionPage) => {
                    const updatedSubmissions = updatePaginatedSubmissions(
                        filteredSubmissionPage,
                        submissionResults,
                        searchType
                    );

                    if (anonymizePersonNames) {
                        updatedSubmissions.items.forEach((submission) => {
                            if (submission.submittedBy?.person) {
                                submission.submittedBy.person = PersonAnonymizer.anonymizePerson(submission.submittedBy.person);
                            }
                        });
                    }

                    setSubmissionResults(updatedSubmissions);
                    setSubmissionFilter(filter);
                })
                .catch((error) => {
                    snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
                })
                .finally(() => {
                    setSubmissionSearchPanelState(SearchPanelState.IDLE);
                });
        },
        [submissionClient, submissionResults, anonymizePersonNames, snackbar, t]
    );

    const countByFilter = useCallback((filter: SubmissionFilterDto) => {
            setSubmissionSearchPanelState(SearchPanelState.SEARCH_IN_PROGRESS);

            return submissionClient.countByFilter(filter)
                .then((filteredSubmissionCount) => {
                    setDisplayedSubmissionsCount(filteredSubmissionCount);
                })
                .catch((error) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error))
                .finally(() => setSubmissionSearchPanelState(SearchPanelState.IDLE));
        },
        [submissionClient, snackbar, t]
    );


    function resetPagingFilter(targetFilter: SubmissionFilterDto, defaultFilter: SubmissionFilterDto) {
        targetFilter.paging = defaultFilter.paging;
    }

    const filterSearch = async () => {
        setSubmissionResults({ items: [], nextPageToken: null });
        setDisplayedSubmissionsCount(0);

        resetPagingFilter(submissionFilter, initialFilter);

        fetchSubmissions(submissionFilter, FilterSearchType.NEW_SEARCH);
        countByFilter(submissionFilter);
    };

    const reloadDefaultSubmissionGrid = useCallback(() => {
        setSubmissionResults({ items: [], nextPageToken: null });
        setDisplayedSubmissionsCount(0);

        resetPagingFilter(submissionFilter, initialFilter);

        fetchSubmissions(submissionFilter, FilterSearchType.NEW_SEARCH);
        countByFilter(submissionFilter);
    }, [fetchSubmissions, countByFilter, submissionFilter, initialFilter]);

    function onFilterChanged(updatedFilter: SubmissionFilterDto) {
        setSubmissionFilter(updatedFilter);
    }

    function hasAnySubmissions(): boolean {
        return submissionResults.items.length > 0;
    }


    return {
        submissionSearchPanelState,
        setSubmissionSearchPanelState,
        submissionFilter,
        setSubmissionFilter,
        submissionResults,
        setSubmissionResults,
        displayedSubmissionsCount,
        setDisplayedSubmissionsCount,
        fetchSubmissions,
        countByFilter,
        filterSearch,
        reloadDefaultSubmissionGrid,
        onFilterChanged,
        hasAnySubmissions,
    };
}
